import * as d3 from 'd3-selection'
import * as dataset from '../../dataset.js'
import _escape from 'lodash.escape'
import * as interactions from '../interactions.js'
import pointerHandler from '../pointer-handler.js'
import { emphasizeMatchedText } from './emphasized-matched-text.js'

const getClasses = function (d) {
  let classes = d.clazz ? [_escape(d.clazz)] : []
  if (d.selected) {
    classes.push('selected')
  }
  if (d.highlighted) {
    classes.push('highlighted')
  }
  if (d.creatable) {
    classes.push('creatable')
  }
  if (d.disabled) {
    classes.push('disabled')
  }
  return classes.join(' ')
}

export default function (selection, data, container) {
  // Generate HTML for each dropdown option
  var html = function (d, i) {
    // Handle creatable options with a special HTML structure
    if (d.creatable) {
      var n = d3.select(document.createElement('div'))

      n.append('svg')
        .attr('class', 'icon-plus')
        .attr('viewBox', '0 0 10 10')
        .append('path')
        .attr('d', 'M4 0v4H0v2h4v4h2V6h4V4H6V0H4z')
      n.append('div')
        .html(data.render.create.bind(null, d, i))

      return n.node().innerHTML
    } else if (data.render.option) {
      // Use custom option renderer if provided, and highlight matched text
      let optionHtml = data.render.option.apply(this, [d, data.searchTerm])
      return emphasizeMatchedText(optionHtml, data.searchTerm)
    } else if (d.native) {
      // For native select options, do not escape the label and highlight matched text
      let label = d[data.accessors.label]
      return emphasizeMatchedText(label, data.searchTerm)
    } else {
      // For non-native options, escape the label and highlight matched text
      let label = _escape(d[data.accessors.label])
      return emphasizeMatchedText(label, data.searchTerm)
    }
  }

  selection.each(function (options) {
    let ul = this
    /*
     * Build an array of li nodes describing the data. We're doing this the ugly way
     * for performance reasons. Even if we use a DocumentFragment in memory to prevent reflows,
     * IE/Edge struggle with `createElement` over and over. This was changed for https://github.com/SpiderStrategies/Scoreboard/issues/22690
     */
    let li = options.map((d, i) => {
      return `<li class="${getClasses(d)}">${html(d, i)}</li>`
    })

    ul.innerHTML = li.join('')

    // Li nodes are in the DOM. Immediately bind data so it's available if some event fires before the rAF.
    // We tried doing this in the rAF but it introduced timing issues. See SB3 #23824
    d3.select(ul)
      .selectAll('li')
      .data(options)

    // Then after all the `li` nodes have been drawn, bind the d3 data and find if a node is selected and scroll to that node.
    window.requestAnimationFrame(() => {
      d3.select(ul)
        .selectAll('li')
        .on('mouseenter.select', function (e, d) {
          // Clear anything that was highlighted. We do this here instead of a mouseleave for two reasons:
          // 1. It's less event listeners, which helps performance when there are 5K+ options
          // 2. If someone keydowns to highlight, then moves the mouse into the menu, there'd be two highlighted options since a mouseleave wouldn't have fired
          d3.select(container)
            .selectAll('li.highlighted')
            .classed('highlighted', false)
            .each(function (d) {
              d.highlighted = false
            })
          d.highlighted = true
          d3.select(this).classed('highlighted', true)
        })
        .on('mousedown.select', pointerHandler(function (e, d) {
          e.preventDefault() // Don't allow events to fire on other elements, we're in control
                                    // and will close the menu at this point
          interactions.select(data, d)
        }))
        .each(function (d) {
          // Set the data-* attributes. We don't do this when constructing the li as a string because escaping is a pain in the ass.
          // Additionally, setting the dataset doesn't trigger reflows
          if (d.dataset) {
            let node = d3.select(this)

            for (const [key, value] of Object.entries(dataset.expand(d.dataset))) {
              node.attr(key, value)
            }
          }
        })
        .filter(d => d.selected)
        .select(function () {
           // The menu is created/destroyed each time the menu opens/closes
           // and if the checked state changes, the menu is always closed. So this is more efficient.
           // On filtering selects, when the user clears the selected option, the svg icon is hidden via css
           d3.select(this)
             .append('svg')
               .attr('class', 'icon-check')
               .attr('viewBox', '0 0 13 9')
               .append('path')
                 .attr('d', 'M12.012 0c-.238 0-.462.1-.612.3L5 6.7c-.735-.73-2.48-2.484-3.4-3.4-.3-.4-.9-.4-1.3 0s-.402.902 0 1.3c1.3 1.283 2.975 2.938 4.114 4.056.38.252.85.295 1.186.043l7.1-7.1c.4-.4.4-.9 0-1.3-.2-.2-.45-.3-.688-.3z')

          let selectedTop = this.offsetTop + ul.parentNode.offsetTop
            , middle = container.offsetHeight / 2

          // Center the select within the container. See Impact #32006
          container.scrollTop = selectedTop - middle + this.offsetHeight / 2
        })
    })
  })
}
